import { chakra } from "@chakra-ui/react";
import type { ReactElement, ReactNode } from "react";
import { type FieldPath, type FieldValues, FormProvider, type UseFormReturn } from "react-hook-form";

import SmartCheckbox, { type SmartCheckboxProps } from "./Checkbox";
import SmartCheckboxGroup, { type SmartCheckboxGroupProps } from "./CheckboxGroup";
import SmartDateInput, { type SmartDateInputProps } from "./DateInput";
import SmartDatePicker, { type SmartDatePickerProps } from "./DatePicker";
import SmartEmailMarkdownInput, { type SmartEmailMarkdownInputProps } from "./EmailMarkdownInput";
import SmartInput, { type SmartInputProps } from "./Input";
import SmartMarkdownInput, { type SmartMarkdownInputProps } from "./MarkdownInput";
import SmartMoneyInput, { type SmartMoneyInputProps } from "./MoneyInput";
import SmartMultiSelect, { type SmartMultiSelectProps } from "./MultiSelect";
import SmartPassword, { type SmartPasswordProps } from "./Password";
import SmartPhoneField, { type SmartPhoneFieldProps } from "./PhoneField";
import SmartRadioGroup, { type SmartRadioGroupProps } from "./RadioGroup";
import SmartSelect, { type SmartSelectProps } from "./Select";
import SmartTextarea, { type SmartTextareaProps } from "./Textarea";

const CForm = chakra("form");

/**
 * The smart field components, each bound to the form's field-value type `T`, so
 * `name` is checked against the form's shape and call sites need neither a
 * per-field generic (`<SmartInput<Values> …>`) nor an import.
 */
export type SmartFields<TFieldValues extends FieldValues = FieldValues> = {
  Input: <TName extends FieldPath<TFieldValues>>(props: SmartInputProps<TFieldValues, TName>) => ReactElement;
  MoneyInput: <TName extends FieldPath<TFieldValues>>(props: SmartMoneyInputProps<TFieldValues, TName>) => ReactElement;
  MarkdownInput: <TName extends FieldPath<TFieldValues>>(
    props: SmartMarkdownInputProps<TFieldValues, TName>,
  ) => ReactElement;
  EmailMarkdownInput: <TName extends FieldPath<TFieldValues>>(
    props: SmartEmailMarkdownInputProps<TFieldValues, TName>,
  ) => ReactElement;
  Textarea: <TName extends FieldPath<TFieldValues>>(props: SmartTextareaProps<TFieldValues, TName>) => ReactElement;
  Password: <TName extends FieldPath<TFieldValues>>(props: SmartPasswordProps<TFieldValues, TName>) => ReactElement;
  PhoneField: <TName extends FieldPath<TFieldValues>>(props: SmartPhoneFieldProps<TFieldValues, TName>) => ReactElement;
  Select: <TName extends FieldPath<TFieldValues>>(props: SmartSelectProps<TFieldValues, TName>) => ReactElement;
  RadioGroup: <TName extends FieldPath<TFieldValues>>(props: SmartRadioGroupProps<TFieldValues, TName>) => ReactElement;
  DateInput: <TName extends FieldPath<TFieldValues>>(props: SmartDateInputProps<TFieldValues, TName>) => ReactElement;
  DatePicker: <TName extends FieldPath<TFieldValues>>(props: SmartDatePickerProps<TFieldValues, TName>) => ReactElement;
  MultiSelect: <TName extends FieldPath<TFieldValues>>(
    props: SmartMultiSelectProps<TFieldValues, TName>,
  ) => ReactElement;
  Checkbox: <TName extends FieldPath<TFieldValues>>(props: SmartCheckboxProps<TFieldValues, TName>) => ReactElement;
  CheckboxGroup: <TName extends FieldPath<TFieldValues>>(
    props: SmartCheckboxGroupProps<TFieldValues, TName>,
  ) => ReactElement;
};

const FIELDS = {
  Input: SmartInput,
  MoneyInput: SmartMoneyInput,
  MarkdownInput: SmartMarkdownInput,
  EmailMarkdownInput: SmartEmailMarkdownInput,
  Textarea: SmartTextarea,
  Password: SmartPassword,
  PhoneField: SmartPhoneField,
  Select: SmartSelect,
  RadioGroup: SmartRadioGroup,
  DateInput: SmartDateInput,
  DatePicker: SmartDatePicker,
  MultiSelect: SmartMultiSelect,
  Checkbox: SmartCheckbox,
  CheckboxGroup: SmartCheckboxGroup,
};

type SmartFormChildren<TFieldValues extends FieldValues> =
  | ReactNode
  | ((fields: SmartFields<TFieldValues>) => ReactNode);

const renderFields = <TFieldValues extends FieldValues>(children: SmartFormChildren<TFieldValues>): ReactNode =>
  typeof children === "function" ? children(FIELDS as unknown as SmartFields<TFieldValues>) : children;

type SmartFormProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  onSubmit: (data: TFieldValues) => void;
  children: SmartFormChildren<TFieldValues>;
} & Omit<React.ComponentProps<typeof CForm>, "children" | "onSubmit">;

/**
 * Renders a `<form>` and provides RHF context. Use when the component owns its
 * form element. `children` may be a plain node or a render function receiving
 * the typed field components:
 *
 * @example
 * <SmartForm form={form} onSubmit={onSubmit}>
 *   {({ Input, PhoneField }) => (
 *     <>
 *       <Input name="email" label="Email" />
 *       <PhoneField name="phone" label="Phone" isRequired />
 *     </>
 *   )}
 * </SmartForm>
 */
const SmartForm = <TFieldValues extends FieldValues = FieldValues>({
  children,
  form,
  onSubmit = () => {},
  ...rest
}: SmartFormProps<TFieldValues>) => {
  return (
    <FormProvider {...form}>
      <CForm {...rest} onSubmit={form.handleSubmit(onSubmit)}>
        {renderFields(children)}
      </CForm>
    </FormProvider>
  );
};

type SmartFormProviderProps<TFieldValues extends FieldValues = FieldValues> = {
  form: UseFormReturn<TFieldValues>;
  children: SmartFormChildren<TFieldValues>;
};

/**
 * Provides RHF context and the typed field components WITHOUT rendering a
 * `<form>` — for forms whose form element is owned by a modal/container that
 * calls `form.handleSubmit` itself.
 *
 * @example
 * <SmartFormProvider form={form}>
 *   {({ Input }) => (
 *     <Modal onSubmit={form.handleSubmit(onSubmit)}>
 *       <Input name="email" label="Email" />
 *     </Modal>
 *   )}
 * </SmartFormProvider>
 */
export const SmartFormProvider = <TFieldValues extends FieldValues = FieldValues>({
  form,
  children,
}: SmartFormProviderProps<TFieldValues>) => {
  return <FormProvider {...form}>{renderFields(children)}</FormProvider>;
};

export default SmartForm;
